import React from 'react';
import {
  Plugins,
  Atom,
  Expressions,
  withSystem,
  type AtomRenderProps,
} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {SHARED_FORM_WRAPPER_CONSTRAINTS_DESCENDANTS} from '@packages/systems/core/constants/SharedConstraintRules';
import {isFormForm} from '@packages/utilities/elementTypeUtils';
import {
  TTypeConstructor,
  TRecord,
  TText,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';
import {TEMP_STATE_PATH} from '@packages/utilities/expression-path-constants';
import {
  EvaluatedNodeData,
  findChild,
  getChildren,
  getInPojo,
} from '@packages/utilities/data-utils';

const FormWrapper = Atom.createRender(
  ({
    attributes,
    classes,
    generateAttributes,
    data,
    context,
    renderChild,
  }: AtomRenderProps) => {
    const formWrapperClasses = classes.push('w-form');
    const formFormChild = findChild<ExpressionElement>(data, isFormForm);
    const formFormElement = formFormChild
      ? (getInPojo(formFormChild, ['props', 'element']) as
          | ExpressionElement
          | undefined)
      : undefined;
    const formState = formFormElement
      ? Expressions.getElementDataAt(
          formFormElement,
          TEMP_STATE_PATH,
          'Enum',
          'normal'
        )
      : 'normal';
    const updatedContext = context.set('formState', formState);

    return (
      <div {...generateAttributes(attributes, formWrapperClasses)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, updatedContext)
        )}
      </div>
    );
  }
);

/**
 * Main create function
 */
const config = createDevlinkAtom({
  constraints: {
    descendants: SHARED_FORM_WRAPPER_CONSTRAINTS_DESCENDANTS,
  },
  displayName: 'Form Block',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('FormBlock')),
  },
  render: FormWrapper,
  showEmpty: true,
  fields: {
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    form: withSystem(
      {private: true, defaultValue: 'wrapper'},
      TRecord({type: TText})
    ),
    children: withSystem(
      {
        labelVariant: 'hidden',
      },
      TTypeConstructor(
        qualify(Plugins.name('Form'), typeName('FormFormChildren'))
      )
    ),
  },
});

export default config;
